import Image from "next/image";
import { MessageSquare } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import {
  websiteBodyText,
  websiteEyebrowText,
  websiteSectionTitleText,
} from "./website-type-scale";

const dataSources = [
  {
    name: "WooCommerce",
    logo: "/website/connectors/woocommerce.svg",
    description: "Your core store — orders, products, revenue, refunds, and margins.",
    questions: [
      "What are my top-selling products this month?",
      "How much revenue did we lose to refunds last quarter?",
      "Which hour of the day drives the most sales?",
    ],
  },
  {
    name: "Google Ads",
    logo: "/website/connectors/google-ads.svg",
    description: "Paid search performance — campaigns, keywords, conversions, and ROAS.",
    questions: [
      "Which campaigns have the highest ROAS this week?",
      "What search terms are driving the most wasted spend?",
      "How do conversions break down by device?",
    ],
  },
  {
    name: "Meta Ads",
    logo: "/website/connectors/meta.svg",
    description: "Facebook and Instagram ad performance — spend, reach, and purchases.",
    questions: [
      "Which ad sets are spending the most with the lowest ROAS?",
      "Which age group drives the most purchases?",
      "How has my daily ad spend trended this month?",
    ],
  },
  {
    name: "Google Analytics 4",
    logo: "/website/connectors/google-analytics.svg",
    description: "Site traffic, user behavior, acquisition sources, and ecommerce conversions.",
    questions: [
      "Where is most of my traffic coming from?",
      "What are my top landing pages this week?",
      "What's my store's current conversion rate?",
    ],
  },
  {
    name: "Google Search Console",
    logo: "/website/connectors/google-search-console.svg",
    description: "Organic search visibility — keywords, rankings, clicks, and indexation.",
    questions: [
      "Which keywords bring the most clicks to my store?",
      "Is this product page indexed and mobile-friendly?",
      "How has my average ranking position changed over 90 days?",
    ],
  },
  {
    name: "Google Merchant Center",
    logo: "/website/connectors/google-merchant-center.svg",
    description: "Shopping feed health — approvals, disapprovals, and price competitiveness.",
    questions: [
      "Which products are disapproved and why?",
      "How does my pricing compare to competitors?",
      "Are there account-level issues affecting my Shopping campaigns?",
    ],
  },
];

export function DataSourcesSection() {
  return (
    <section className="py-24 bg-slate-950 border-t border-slate-900/50">
      <div className="container mx-auto px-4 md:px-6">
        <ScrollReveal>
          <div className="text-center mb-16">
            <p className={`mb-4 text-purple-300 ${websiteEyebrowText}`}>Data Sources</p>
            <h2 className={`mx-auto max-w-3xl text-white ${websiteSectionTitleText}`}>
              Six connectors. Every question answered.
            </h2>
            <p className={`mx-auto mt-5 max-w-xl text-slate-400 ${websiteBodyText}`}>
              Storita pulls live data from six sources. Ask anything about any of them — in plain language.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dataSources.map((source, i) => (
            <ScrollReveal key={source.name} delay={i * 80}>
              <div className="flex flex-col h-full p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-colors duration-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center">
                    <Image
                      src={source.logo}
                      alt={source.name}
                      width={40}
                      height={40}
                      className="h-8 w-8 object-contain"
                    />
                  </div>
                  <h3 className="text-base font-semibold text-white">{source.name}</h3>
                </div>

                <p className="text-sm text-slate-400 leading-relaxed mb-5">{source.description}</p>

                <div className="mt-auto flex flex-col gap-2">
                  {source.questions.map((q) => (
                    <div
                      key={q}
                      className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-slate-950 border border-slate-800/70 text-slate-300 text-sm leading-snug"
                    >
                      <MessageSquare className="h-3.5 w-3.5 text-purple-400 mt-0.5 shrink-0" />
                      <span>"{q}"</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
